import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

const MAX_CLAPS = 20;

export const ClapButton = ({ slug, title }: { slug: string; title?: string }) => {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["reactions", slug],
    queryFn: async () => (await api.get(`/articles/${slug}/reactions`)).data,
    staleTime: 60 * 1000,
  });
  const [pending, setPending] = useState(0);
  const [burst, setBurst] = useState(false);
  const deltaRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = async () => {
    const n = deltaRef.current;
    if (!n) return;
    deltaRef.current = 0;
    setPending(0);
    try {
      const { data: res } = await api.post(`/articles/${slug}/reactions`, { count: n });
      queryClient.setQueryData(["reactions", slug], res);
    } catch { /* keep silent */ }
  };

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    if (deltaRef.current) {
      api.post(`/articles/${slug}/reactions`, { count: deltaRef.current }).catch(() => {});
    }
  }, [slug]);

  const mine = (data?.mine ?? 0) + pending;
  const total = (data?.claps ?? 0) + pending;
  const maxed = mine >= MAX_CLAPS;

  const clap = () => {
    if (maxed) return;
    deltaRef.current += 1;
    setPending(deltaRef.current);
    setBurst(true);
    setTimeout(() => setBurst(false), 250);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 700);
  };

  const shareOnX = () => {
    const url = `${window.location.origin}/article/${slug}`;
    const text = title ? `👏 Worth a read: "${title}"` : "👏 Worth a read";
    api.post("/analytics/track", { event_type: "article_share", metadata: { slug, channel: "x" } }).catch(() => {});
    window.open(`https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-10">
      <div className="flex items-center justify-center gap-4" data-testid="clap-row">
      <button onClick={clap} data-testid="clap-button" aria-label="Applaud this story"
        title={maxed ? "You've given your maximum applause — thank you!" : "Applaud this story (tap repeatedly)"}
        className={`group relative flex h-14 w-14 items-center justify-center rounded-full border transition-all duration-200 ${
          mine > 0
            ? "border-[#7C3AED] bg-[#7C3AED]/20 shadow-[0_0_18px_rgba(124,58,237,0.35)]"
            : "border-white/15 bg-white/[0.03] hover:border-[#7C3AED]/60 hover:bg-[#7C3AED]/10"
        } ${burst ? "scale-110" : "scale-100"} ${maxed ? "cursor-default" : "active:scale-95"}`}>
        <span className={`text-2xl transition-transform duration-200 ${burst ? "-rotate-12 scale-110" : ""}`} aria-hidden>👏</span>
        {burst && (
          <span className="pointer-events-none absolute -top-2 right-0 font-mono text-[11px] font-bold text-[#00E5FF] animate-[fadeUp_0.5s_ease-out]" aria-hidden>
            +1
          </span>
        )}
      </button>
      <div>
        <p className="font-heading font-bold text-white text-lg leading-none" data-testid="clap-total">{total.toLocaleString()}</p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
          {maxed ? "Max applause given" : mine > 0 ? `Applause · ${mine} from you` : "Applause"}
        </p>
      </div>
      </div>
      {maxed && (
        <div className="mt-4 flex justify-center animate-[fadeIn_0.4s_ease-out]" data-testid="clap-share-prompt">
          <button onClick={shareOnX} data-testid="clap-share-x"
            className="group inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-4 py-2 text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-[#00E5FF]/20 hover:border-[#00E5FF]/70">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current text-[#00E5FF]" aria-hidden>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Loved it that much? Share it on X
            <span className="transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden>→</span>
          </button>
        </div>
      )}
    </div>
  );
};
